import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Globe, History, Zap, Shield, ArrowRight, Terminal, Box, Activity, Cpu, Settings, ArrowLeft } from 'lucide-react';
import { cn } from '../lib/utils';
import { Network } from '../types';
import { useSettings } from '../contexts/SettingsContext';

export const AppHome: React.FC = () => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [txHash, setTxHash] = useState('');
  const [network, setNetwork] = useState<Network>('ethereum');
  const [isForkMode, setIsForkMode] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [apiKey, setApiKey] = useState(localStorage.getItem('oru_api_key') || '');
  const [provider, setProvider] = useState(localStorage.getItem('oru_provider') || 'infura');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (txHash) {
      localStorage.setItem('oru_api_key', apiKey);
      localStorage.setItem('oru_provider', provider);
      
      const searchParams = new URLSearchParams();
      if (apiKey) searchParams.set('apiKey', apiKey);
      if (provider) searchParams.set('provider', provider);
      
      const query = searchParams.toString();
      navigate(`/app/tx/${network}/${txHash}${query ? '?' + query : ''}`);
    }
  };

  const recentTraces = [
    { hash: '0x1234...5678', network: 'mainnet', time: '2 mins ago', status: 'success' },
    { hash: '0xabcd...ef01', network: 'arbitrum', time: '15 mins ago', status: 'reverted' },
    { hash: '0x9876...5432', network: 'optimism', time: '1 hour ago', status: 'success' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center p-8 relative overflow-hidden">
      {/* Background Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
      
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/')}
        className="absolute top-32 left-8 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-[#525252] hover:text-white transition-colors group z-20"
      >
        <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
        Exit Application
      </motion.button>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-4xl z-10"
      >
        <div className="text-center mb-16">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/5 border border-emerald-500/10 text-emerald-500 text-[10px] font-bold uppercase tracking-[0.3em] mb-8"
          >
            <Terminal className="w-3 h-3" />
            EVM Execution Engine
          </motion.div>
          <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold tracking-tighter text-white mb-6 uppercase leading-none">
            ORU <span className="text-emerald-500">TRACE</span>
          </h1>
          <p className="text-white/40 font-medium text-base sm:text-lg max-w-xl mx-auto px-4">
            The professional standard for transaction debugging. Enter a hash to begin deep execution analysis.
          </p>
        </div>

        <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl sm:rounded-3xl p-4 sm:p-8 shadow-2xl shadow-black/50 backdrop-blur-xl">
          <form onSubmit={handleSearch} className="space-y-6 sm:space-y-8">
            <div className="relative">
              <div className="absolute inset-y-0 left-4 sm:left-6 flex items-center pointer-events-none">
                <Search className="w-4 h-4 sm:w-5 sm:h-5 text-white/20" />
              </div>
              <input
                type="text"
                placeholder="Transaction Hash (0x...)"
                value={txHash}
                onChange={(e) => setTxHash(e.target.value)}
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl sm:rounded-2xl pl-12 sm:pl-16 pr-4 sm:pr-6 py-4 sm:py-6 text-base sm:text-xl font-mono text-white placeholder:text-white/10 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/50 transition-all"
              />
            </div>

            <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-6">
              <div className="flex flex-wrap gap-1.5 p-1.5 bg-white/[0.03] border border-white/5 rounded-xl sm:rounded-2xl justify-center md:justify-start">
                {(['ethereum', 'linea', 'arbitrum', 'optimism', 'base', 'polygon'] as Network[]).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setNetwork(n)}
                    className={cn(
                      "px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-[9px] sm:text-[10px] font-bold uppercase tracking-widest transition-all",
                      network === n 
                        ? "bg-emerald-500 text-black shadow-[0_0_20px_rgba(16,185,129,0.3)]" 
                        : "text-white/40 hover:text-white hover:bg-white/5"
                    )}
                  >
                    {n}
                  </button>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className={cn(
                    "px-6 py-3 rounded-xl border transition-all flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-widest",
                    showAdvanced 
                      ? "bg-white/10 border-white/30 text-white" 
                      : "bg-white/[0.03] border-white/10 text-white/40 hover:border-white/20"
                  )}
                >
                  <Settings className="w-3.5 h-3.5" />
                  Advanced
                </button>
                <button
                  type="submit"
                  className="bg-white text-black hover:bg-emerald-500 font-bold px-10 py-3 rounded-xl transition-all text-[10px] uppercase tracking-[0.2em] flex items-center justify-center gap-2 group"
                >
                  Trace
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>

            {/* Advanced Settings Panel */}
            <AnimatePresence>
              {showAdvanced && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="pt-8 border-t border-white/5 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="md:col-span-1 space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-white/40 ml-1">Provider</label>
                        <select
                          value={provider}
                          onChange={(e) => setProvider(e.target.value)}
                          className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm font-mono text-white focus:outline-none focus:border-emerald-500/50 transition-all"
                        >
                          <option value="infura">Infura</option>
                          <option value="alchemy">Alchemy</option>
                          <option value="custom">Custom RPC URL</option>
                        </select>
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-white/40 ml-1">
                          {provider === 'custom' ? 'Full RPC URL' : 'Project API Key'}
                        </label>
                        <input
                          type="password"
                          placeholder={provider === 'custom' ? 'https://your-rpc-url.com' : 'Enter API Key...'}
                          value={apiKey}
                          onChange={(e) => setApiKey(e.target.value)}
                          className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm font-mono text-white focus:outline-none focus:border-emerald-500/50 transition-all"
                        />
                      </div>
                    </div>
                    <div className="flex gap-4 p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/10">
                      <Zap className="w-4 h-4 text-emerald-500 shrink-0" />
                      <p className="text-[10px] text-white/40 leading-relaxed uppercase tracking-tighter">
                        <span className="text-emerald-500 font-bold">Heuristic Trace:</span> We prioritize your local API key for full trace data. If missing, we'll attempt a secure fallback to our global node before reverting to lite mode.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </form>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <h3 className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/20 mb-6 flex items-center gap-2">
              <History className="w-4 h-4" />
              Recent Activity
            </h3>
            <div className="space-y-3">
              {recentTraces.map((trace, i) => (
                <button
                  key={i}
                  onClick={() => navigate(`/app/tx/${trace.network}/${trace.hash}`)}
                  className="w-full flex items-center justify-between p-4 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-emerald-500/30 hover:bg-white/[0.04] transition-all group"
                >
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "w-2 h-2 rounded-full",
                      trace.status === 'success' ? "bg-emerald-500" : "bg-red-500"
                    )} />
                    <div className="flex flex-col items-start">
                      <span className="text-xs font-mono text-white/80 group-hover:text-white transition-colors">{trace.hash}</span>
                      <span className="text-[9px] font-bold uppercase tracking-widest text-white/20">{trace.network}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-[9px] font-mono text-white/10">{trace.time}</span>
                    <ArrowRight className="w-3 h-3 text-white/10 group-hover:text-emerald-500 transition-colors" />
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-8">
            <section>
              <h3 className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/20 mb-6 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Security
              </h3>
              <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-4">
                <div className="flex items-center gap-3">
                  <Cpu className="w-4 h-4 text-emerald-500" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white/60">Isolated Engine</span>
                </div>
                <p className="text-[10px] text-white/30 font-medium leading-relaxed">
                  Traces are executed in a sandboxed environment with real-time state isolation.
                </p>
              </div>
            </section>

            <section>
              <h3 className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/20 mb-6 flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Network Status
              </h3>
              <div className="flex items-center justify-between p-4 rounded-2xl bg-emerald-500/5 border border-emerald-500/10">
                <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-500">All Nodes Operational</span>
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              </div>
            </section>
          </div>
        </div>
      </motion.div>

      {settings.telemetry && (
        <div className="absolute bottom-4 right-4 flex items-center gap-2 text-[10px] font-mono text-emerald-500/30 uppercase tracking-[0.2em] pointer-events-none">
          <Activity className="w-3 h-3 animate-pulse" />
          Telemetry Pipeline Active
        </div>
      )}
    </div>
  );
};
